"use client";

import * as React from "react";
import Link from "next/link";
import {
  Ban,
  Check,
  Clock,
  Compass,
  Copy,
  ExternalLink,
  ImageIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Public verification page — the only page most people ever see, usually from a
 * scanned QR on a printed certificate.
 *
 * Rules baked in:
 * - Mobile-first single column; two columns only from md, sharing the top baseline.
 * - Above the fold: status, holder, competency, issuer. Nothing else competes.
 * - Body ink is FIXED (--foreground). The tenant tint only paints accents, so a tenant
 *   can never make this page unreadable.
 * - The verification number is provenance, not the headline: truncated, monospace,
 *   with copy + explorer.
 */

export type VerificationStatus = "VALID" | "EXPIRED" | "REVOKED";

export type VerificationData = {
  issuer: { name: string; initials: string; logoUrl?: string; brandTint: string };
  holderName: string;
  competency: string;
  issuedAtLabel: string;
  expiresAtLabel: string | null;
  /** Human phrase: "Valid until 31 December 2030" / "Expired 2 years ago · 9 February 2024" */
  statusPhrase: string;
  status: VerificationStatus;
  artworkUrl?: string;
  /** Image src for the QR, when one exists server-side. */
  qrUrl?: string;
  /** Rendered QR element. Takes precedence over qrUrl. */
  qrNode?: React.ReactNode;
  chain: { contract: string; tokenId: string; chainId: string; explorerUrl: string };
};

const STATUS: Record<
  VerificationStatus,
  { label: string; icon: React.ComponentType<{ className?: string }>; wrap: string; ink: string; note: string }
> = {
  VALID: {
    label: "Verified certificate",
    icon: Check,
    wrap: "border-success-border bg-success",
    ink: "text-success-foreground",
    note: "This certificate is active and its record matches the blockchain.",
  },
  EXPIRED: {
    label: "Expired certificate",
    icon: Clock,
    wrap: "border-accent-foreground/20 bg-accent",
    ink: "text-accent-foreground",
    note: "It was genuinely issued, but the competency period has ended. Ask the holder for a current certificate.",
  },
  REVOKED: {
    label: "Revoked certificate",
    icon: Ban,
    wrap: "border-destructive/25 bg-destructive/10",
    ink: "text-destructive",
    note: "The issuing organisation withdrew this certificate. It should not be accepted as proof of competency.",
  },
};

const shortAddress = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;

function IssuerBar({ issuer }: { issuer: VerificationData["issuer"] }) {
  return (
    // white, always — a transparent logo must never sit on a dark wash it wasn't made for
    <header className="border-b bg-white">
      <div className="mx-auto flex max-w-[960px] items-center gap-3 px-5 py-4 md:px-6">
        {issuer.logoUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={issuer.logoUrl} alt="" className="max-h-9 max-w-28 object-contain" />
        ) : (
          <span
            style={{ backgroundColor: issuer.brandTint }}
            className="flex size-9 items-center justify-center rounded-[10px] text-[13px] font-semibold text-white"
          >
            {issuer.initials}
          </span>
        )}
        <span className="flex flex-1 flex-col">
          <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Issued by
          </span>
          <span className="text-[15px] font-semibold text-foreground">{issuer.name}</span>
        </span>
        <span className="hidden items-center gap-1.5 md:flex">
          <span className="text-xs text-muted-foreground">Powered by</span>
          <span className="text-xs font-semibold text-primary">sigalmark™</span>
        </span>
      </div>
    </header>
  );
}

function StatusBlock({ data }: { data: VerificationData }) {
  const s = STATUS[data.status];
  return (
    <div className={cn("flex items-center gap-3.5 rounded-xl border p-4 md:px-5", s.wrap)}>
      <span
        className={cn(
          "flex size-11 shrink-0 items-center justify-center rounded-xl bg-white md:size-12",
          s.ink
        )}
      >
        <s.icon className="size-6" />
      </span>
      <span className="flex flex-col gap-0.5">
        <span className={cn("text-[17px] font-semibold md:text-xl", s.ink)}>{s.label}</span>
        <span className={cn("text-[13px] opacity-85 md:text-sm", s.ink)}>
          {data.statusPhrase}
        </span>
      </span>
    </div>
  );
}

function Provenance({ data }: { data: VerificationData }) {
  const full = `${data.chain.contract}/${data.chain.tokenId}/${data.chain.chainId}`;
  return (
    <div className="flex flex-col gap-3.5 rounded-xl border bg-card p-4 md:p-5">
      <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
        Provenance
      </span>
      <div className="flex items-center gap-3">
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-xs text-muted-foreground">Verification number</span>
          <span className="whitespace-nowrap font-mono text-[13px] md:text-sm">
            {shortAddress(data.chain.contract)} · {data.chain.tokenId}
          </span>
        </span>
        <button
          type="button"
          onClick={() => void navigator.clipboard.writeText(full)}
          className="flex h-9 items-center gap-1.5 rounded-[9px] border bg-background px-3 text-[13px] hover:bg-accent"
        >
          <Copy className="size-3.5 text-muted-foreground" />
          <span className="hidden sm:inline">Copy</span>
        </button>
        <a
          href={data.chain.explorerUrl}
          target="_blank"
          rel="noreferrer"
          style={{ color: data.issuer.brandTint }}
          className="flex items-center gap-1 whitespace-nowrap text-[13px] font-medium hover:underline"
        >
          Explorer
          <ExternalLink className="size-3.5" />
        </a>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 border-t pt-3.5 text-[13px] text-muted-foreground">
        <span>Contract {shortAddress(data.chain.contract)}</span>
        <span>Token {data.chain.tokenId}</span>
        <span>Chain {data.chain.chainId}</span>
      </div>
    </div>
  );
}

export function VerificationPage({ data }: { data: VerificationData }) {
  return (
    <main className="min-h-dvh bg-background text-foreground">
      <IssuerBar issuer={data.issuer} />

      <div className="mx-auto grid max-w-[960px] gap-8 px-5 pb-12 pt-6 md:grid-cols-[380px_minmax(0,1fr)] md:items-start md:px-6 md:pt-8">
        {/* mobile order: status + facts first, artwork after */}
        <div className="order-2 flex flex-col gap-4 md:order-1">
          {data.artworkUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={data.artworkUrl}
              alt={`Certificate for ${data.holderName}`}
              className="w-full rounded-xl border object-cover"
            />
          ) : (
            <div className="flex h-52 flex-col items-center justify-center gap-2 rounded-xl border bg-card md:h-[270px]">
              <span className="flex size-9 items-center justify-center rounded-[10px] bg-accent">
                <ImageIcon className="size-[19px] text-accent-foreground" />
              </span>
              <span className="text-[13px] text-muted-foreground">Certificate artwork</span>
            </div>
          )}
          <div className="flex flex-col items-center gap-2 rounded-xl border bg-white p-4 text-center">
            {data.qrNode ? (
              <span className="flex shrink-0 items-center justify-center">
                {data.qrNode}
              </span>
            ) : data.qrUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={data.qrUrl} alt="" className="size-16 shrink-0 rounded-md" />
            ) : null}
            <span className="flex flex-col gap-0.5">
              <span className="text-[13px] font-semibold text-foreground">
                Same code, same page
              </span>
              <span className="text-xs leading-snug text-muted-foreground">
                Scanning the certificate’s QR brings anyone here.
              </span>
            </span>
          </div>
        </div>

        <div className="order-1 flex min-w-0 flex-col gap-6 md:order-2">
          <StatusBlock data={data} />

          <div className="flex flex-col gap-2.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              Certificate holder
            </span>
            <h1 className="text-[26px] font-semibold leading-tight tracking-[-0.015em] text-pretty md:text-[34px]">
              {data.holderName}
            </h1>
            <p className="text-base leading-snug text-foreground/80 text-pretty md:text-lg">
              {data.competency}
            </p>
            <p className="max-w-[56ch] text-sm leading-relaxed text-muted-foreground text-pretty">
              {STATUS[data.status].note}
            </p>
          </div>

          <dl className="grid grid-cols-2 gap-4 border-t pt-5">
            <div className="flex flex-col gap-0.5">
              <dt className="text-[13px] text-muted-foreground">Issued</dt>
              <dd className="text-[15px]">{data.issuedAtLabel}</dd>
            </div>
            <div className="flex flex-col gap-0.5">
              <dt className="text-[13px] text-muted-foreground">Expiry</dt>
              <dd className="text-[15px]">{data.expiresAtLabel ?? "No expiry"}</dd>
            </div>
          </dl>

          <Provenance data={data} />
        </div>
      </div>

      <footer className="flex items-center justify-center gap-1.5 border-t px-5 py-5 md:hidden">
        <span className="text-xs text-muted-foreground">Powered by</span>
        <span className="text-xs font-semibold text-primary">sigalmark™</span>
      </footer>
    </main>
  );
}

export function VerificationNotFound({ code }: { code: string }) {
  return (
    <main className="flex min-h-dvh flex-col bg-background text-foreground">
      <header className="flex items-center gap-2 border-b bg-white px-5 py-4">
        <span className="flex size-[26px] items-center justify-center rounded-[7px] bg-accent">
          <svg viewBox="0 0 24 24" className="size-4 text-accent-foreground" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="8" r="6" />
            <path d="M8.2 13.9 7 22l5-3 5 3-1.2-8.1" />
          </svg>
        </span>
        <span className="text-sm font-semibold">Sigalmark verification</span>
      </header>

      <div className="flex flex-1 flex-col items-center justify-center gap-3.5 px-6 py-12 text-center">
        <span className="flex size-13 items-center justify-center rounded-[14px] border bg-card p-3 text-muted-foreground">
          <Compass className="size-6" />
        </span>
        <h1 className="text-[22px] font-semibold">No certificate at this link</h1>
        <p className="max-w-[46ch] text-sm leading-relaxed text-muted-foreground text-pretty">
          Nothing matches{" "}
          <span className="rounded border bg-card px-1.5 py-0.5 font-mono text-[13px]">{code}</span>.
          Verification links are case-sensitive, so check the last few characters against the
          certificate.
        </p>
        <div className="mt-1 flex w-full max-w-xs flex-col gap-2.5">
          <Link
            href="/verify"
            className="flex h-11 items-center justify-center rounded-[10px] bg-primary text-[15px] font-semibold text-primary-foreground hover:bg-primary/90"
          >
            Enter a code manually
          </Link>
          <span className="text-[13px] text-muted-foreground">
            Or rescan the QR code on the certificate.
          </span>
        </div>
      </div>

      <footer className="flex items-center justify-center gap-1.5 border-t px-5 py-4">
        <span className="text-xs text-muted-foreground">Powered by</span>
        <span className="text-xs font-semibold text-primary">sigalmark™</span>
      </footer>
    </main>
  );
}
